import { ThemeOptions } from '@mui/material';
import createBreakpoints from '@mui/system/createTheme/createBreakpoints';
import variables from 'styles/config.scss';

const appBreakpoints = {
  values: {
    xs: Number(variables.xs),
    sm: Number(variables.sm),
    md: Number(variables.md),
    lg: Number(variables.lg),
    xl: Number(variables.xl),
  },
};

const breakpoints = createBreakpoints({ ...appBreakpoints });

export const AppThemeOptions: ThemeOptions = {
  breakpoints: breakpoints,
  spacing: 10,

  typography: {
    fontFamily: 'Arial, Tahoma, Geneva, sans-serif',
    fontSize: 14,
    htmlFontSize: 10,
    h1: {
      fontSize: '4rem',
      lineHeight: 1.2,
      fontWeight: 'normal',
    },
    h2: {
      fontSize: '2.4rem',
      lineHeight: 1.33,
      fontWeight: 500,
    },
    h3: {
      fontSize: '2rem',
      lineHeight: 1.2,
      fontWeight: 500,
    },

    subtitle1: {
      fontSize: '1.6rem',
      lineHeight: 1.2,
      fontWeight: 500,
    },
    subtitle2: {
      fontSize: '1.4rem',
      lineHeight: 1.4,
      fontWeight: 500,
    },
    body1: {
      fontSize: '1.4rem',
      lineHeight: 1.2,
    },
    body2: {
      fontSize: '1.4rem',
      lineHeight: 1.4,
    },
    button: {
      fontSize: '1.4rem',
      fontWeight: 700,
      lineHeight: 1.14,
      textTransform: 'inherit',
    },
    caption: {
      fontSize: '1.2rem',
      lineHeight: 1.25,
    },
  },

  palette: {
    primary: {
      main: variables.colorPrimary,
      contrastText: '#fff',
    },
    secondary: {
      main: variables.colorSecondary,
      dark: variables.colorSecondaryDark,
      contrastText: '#fff',
    },
    success: {
      main: variables.colorSecondary,
    },
    error: {
      main: variables.colorRed,
    },
    text: {
      primary: variables.colorGreyDark,
      secondary: variables.colorPrimary,
    },

    tonalOffset: 0.05,
  },

  components: {
    MuiButton: {
      defaultProps: {
        disableElevation: true,
      },
      styleOverrides: {
        containedPrimary: {
          borderRadius: '.4rem',
        },
        root: {},
        sizeLarge: {
          minHeight: '4.8rem',
        },
        sizeMedium: {
          minHeight: '3.6rem',
        },
        sizeSmall: {
          fontSize: '1.2rem',
          lineHeight: 1,
        },
      },
    },

    MuiTextField: {
      defaultProps: {
        variant: 'outlined',
        color: 'secondary',
        size: 'small',
        autoComplete: 'off',
      },
    },

    MuiInputBase: {
      styleOverrides: {
        input: {
          height: '1.8em',
        },
      },
    },

    MuiFormLabel: {
      styleOverrides: {
        root: {
          lineHeight: '1.8em',
        },
      },
    },

    MuiInput: {
      styleOverrides: {
        underline: {
          '&::before': {
            borderBottomColor: variables.colorGreyLines,
          },
        },
      },
    },

    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 0,
        },
        notchedOutline: {
          borderColor: '#E9EEF0',
        },
      },
    },

    MuiInputLabel: {
      styleOverrides: {
        root: {
          color: variables.colorGrey,
          letterSpacing: '0.04rem',
        },
        shrink: {
          color: variables.colorGrey,
        },
      },
    },

    MuiFormHelperText: {
      styleOverrides: {
        root: {
          fontSize: '1rem',
          marginTop: '0.2rem',
        },
      },
    },

    MuiAutocomplete: {
      styleOverrides: {
        listbox: {
          fontSize: '1.4rem',
        },
      },
    },

    MuiSvgIcon: {
      styleOverrides: {
        root: {
          fontSize: '2.4rem',
        },
        fontSizeSmall: {
          fontSize: '1.5rem',
        },
      },
    },

    MuiPaginationItem: {
      styleOverrides: {
        root: {
          '&.Mui-selected': {
            backgroundColor: 'transparent',
            color: variables.colorSecondary,
            fontWeight: 700,
            '&:hover': {
              color: '#fff',
            },
          },
        },
        icon: {
          fontSize: '2.2rem',
        },
      },
    },

    MuiPopover: {},

    MuiCheckbox: {
      defaultProps: {
        color: 'secondary',
      },
    },

    MuiIconButton: {
      styleOverrides: {},
    },

    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: '0.8rem',
        },
        paperFullScreen: {
          borderRadius: '0',
        },
      },
    },

    MuiDialogContent: {
      styleOverrides: {
        root: {
          padding: '2rem ',
        },
      },
    },

    MuiDialogActions: {
      styleOverrides: {
        root: {
          padding: '0 2rem 2rem',
        },
      },
    },

    MuiDialogTitle: {
      styleOverrides: {
        root: {
          padding: '1.6rem 2rem',
        },
      },
    },

    MuiPaper: {
      styleOverrides: {
        root: {
          boxShadow: variables.boxShadowDarkBlue,
          borderRadius: '0',
        },
      },
    },

    MuiToolbar: {
      styleOverrides: {
        gutters: {
          [breakpoints.up('xs')]: {
            paddingLeft: '1.6rem',
            paddingRight: '1.6re',
          },
          [breakpoints.up('md')]: {
            paddingLeft: '2rem',
            paddingRight: '2rem',
          },
        },
      },
    },

    MuiContainer: {
      styleOverrides: {
        root: {
          paddingLeft: '1.6rem',
          paddingRight: '1.6rem',
          [breakpoints.up('sm')]: {
            paddingLeft: '1.6rem',
            paddingRight: '1.6rem',
          },
          [breakpoints.up('md')]: {
            paddingLeft: '1.6rem',
            paddingRight: '1.6rem',
          },
          [breakpoints.up('lg')]: {
            paddingLeft: '2.4rem',
            paddingRight: '2.4rem',
          },
        },
      },
    },

    MuiDivider: {
      styleOverrides: {
        root: {
          borderColor: variables.colorGreyLines,
        },
      },
    },
  },
};
